import { BigNumber } from 'ethers'
import { formatUnits } from 'ethers/lib/utils'
import { MatchPoolStake, Winner } from '~/domain/MatchPool'

interface Claim {
  amount: BigNumber
  homeAmount: BigNumber
  awayAmount: BigNumber
  pnl: BigNumber
}

export function getRewards(
  userStaked: BigNumber,
  totalStaked: BigNumber,
  totalRewards: BigNumber
): BigNumber {
  return totalStaked.gt(0)
    ? totalRewards.mul(userStaked).div(totalStaked)
    : BigNumber.from(0)
}

function getRate(
  userStaked: BigNumber,
  totalStaked: BigNumber,
  totalRewards: BigNumber,
  feePercentage: BigNumber
): number {
  const decimals = 18
  const rewards = getRewards(userStaked, totalStaked, totalRewards)
  const fees = userStaked.mul(feePercentage).div(100)
  const result = userStaked.add(rewards).sub(fees)
  const benefit = result.sub(userStaked)

  const benefitFloat = parseFloat(formatUnits(benefit, decimals))
  const userStakedFloat = parseFloat(formatUnits(userStaked, decimals))

  return benefitFloat / userStakedFloat
}

export function getYield(
  userStaked: BigNumber,
  totalStaked: BigNumber,
  totalRewards: BigNumber,
  feePercentage: BigNumber
): number {
  const rate = getRate(userStaked, totalStaked, totalRewards, feePercentage)

  const period = 365

  return (1 + rate)**period - 1
}

export function getClaim(
  homeUserStakes: MatchPoolStake | undefined,
  awayUserStakes: MatchPoolStake | undefined,
  homeTotalStaked: BigNumber,
  awayTotalStaked: BigNumber,
  totalRewards: BigNumber,
  feePercentage: BigNumber,
  winner: Winner
): Claim {
  const homeStakesAmount = homeUserStakes?.amount ? homeUserStakes.amount : BigNumber.from(0)
  const awayStakesAmount = awayUserStakes?.amount ? awayUserStakes.amount : BigNumber.from(0)
  const homeFees = homeStakesAmount.mul(feePercentage).div(100)
  const awayFees = awayStakesAmount.mul(feePercentage).div(100)
  const homeRewards = winner === Winner.HOME
    ? getRewards(homeStakesAmount, homeTotalStaked, totalRewards)
    : BigNumber.from(0)
  const awayRewards = winner === Winner.AWAY
    ? getRewards(awayStakesAmount, awayTotalStaked, totalRewards)
    : BigNumber.from(0)

  const homeAmount = homeStakesAmount
    .sub(homeFees)
    .add(homeRewards)

  const awayAmount = awayStakesAmount
    .sub(awayFees)
    .add(awayRewards)

  const amount = homeAmount.add(awayAmount)

  const pnl = amount.sub(homeStakesAmount).sub(awayStakesAmount)

  return { amount, homeAmount, awayAmount, pnl }
}
